<template>
    <span>
        <OmniButton v-if="showButton" v-bind="$props"
                    @click="printPackingSlip" :variant="variant"
                    :disabled="isPackingSlipRequested"
                    :title="title"
        >
            <template #icon>
                <font-awesome-icon v-if="!isPackingSlipRequested" icon="print" :class="size" />
                <font-awesome-icon v-else icon="spinner" spin v-bind="[$attrs]"/>
            </template>

            <slot>
                <span v-if="!isPackingSlipRequested" v-bind="[$attrs]">{{ title }}</span>
            </slot>
        </OmniButton>
    </span>
</template>

<style scoped>
  .xxl {
    width: 32px;
    height: 32px;
  }
</style>

<script>
import _ from "lodash"

const ALLOW_STATUSES = [
    'requested',
    'shipped',  // Willen we een nieuw label kunnen aanmaken voor het geval dat de carrier het product niet aflevert en direct terugstuurt naar de afzender.
]

const SHOW_FOR_DISPATCH_TYPES = [
    'click_and_collect',
    'ship_from_store',
    'dc_delivery',
]

import OmniButton from '@/components/OmniStock/OmniButton.vue';
import {dispatchRequestStatus} from "../../../helpers/dispatch";
import {downloadPackingSlipButtonBase} from "./downloadPackingSlipButtonBase";

export default {
    name: "PrintLabelsButton",
    extends: downloadPackingSlipButtonBase,

    components: {
        OmniButton,
    },

    props: {
        dispatchRequest: {required: true},
        variant: {default: 'link'},
        askLabelCount: {default: false},
        size: {default: 'lg'},
    },

    data() {
        return {
            isPackingSlipRequested: false,
        }
    },

    computed: {
        title() {
            let printTypes = []
            if (this.isAutoPrintLabelsEnabled) printTypes.push('labels')
            if (this.isAutoPrintPackingSlipEnabled) printTypes.push('pakbon')

            return `Print ${printTypes.join(' en ')}`
        },
        uniqueTrackingCodes() {
            return _.uniq((this.dispatchRequest.tracking_codes ?? []).map(t => t.code)).length
        },
        showButton() {
            return SHOW_FOR_DISPATCH_TYPES.includes(this.dispatchRequest.dispatch_type?.identifier)
                && (
                    this.isAutoPrintLabelsEnabled
                    || this.isAutoPrintPackingSlipEnabled
                )
        },
    },

    mounted() {
        this.requestLabelCount = this.minimumLabelCount
    },

    methods: {
        printPackingSlip() {
            if (this.isPackingSlipRequested) {
                return
            }

            if (this.dispatchRequest.carrier_method && !this.dispatchRequest.carrier_method.has_label_config) {
                this.$alert('De orderbon kan niet geprint worden omdat deze vervoerder/bezorgwijze daar niet voor is geconfigureerd.', 'danger')
                return
            }

            if (ALLOW_STATUSES.indexOf(this.dispatchRequest.status) === -1) {
                this.$alert(`De orderbon kan niet gedownload worden voor een levering met de status '${dispatchRequestStatus(this.dispatchRequest.status)}'`, 'danger')
                return
            }

            this.isPackingSlipRequested = true
            this.labelCountModalVisible = false;

            this.$http.get(
                `/api/rest/dispatch-requests/id:${this.dispatchRequest.id}/packing-slip`,
                {
                    params: {
                        separate_label: 1,
                        auto_print_label: this.isAutoPrintLabelsEnabled ? 1 : 0,
                        auto_print_packing_slip: this.isAutoPrintPackingSlipEnabled ? 1 : 0,
                        label_count: (this.uniqueTrackingCodes) ? this.uniqueTrackingCodes : 1,
                        ignore_cache: 0,
                    }
                }
            )
                .then(response => {
                    if (this.isAutoPrintPackingSlipEnabled) {
                        this.$alert(`Er is een automatische printopdracht verstuurd voor de pakbon`, 'info')
                    }

                    if (response.data['labels-content-type']) {
                        _.each(response.data.labels ?? {}, (label, barcode) => {
                            if (this.isAutoPrintLabelsEnabled) {
                                this.$alert(`Er is een automatische printopdracht verstuurd voor het label met barcode: ${barcode}`, 'info')
                            }
                        })
                    } else {
                        this.$alert(`Er zijn geen labels om te printen`, 'info')
                    }
                })
                .catch(e => {
                    console.error(e)

                    switch (e.response.data?.errors?.[0]?.identifier) {
                        case '100':
                            this.$alert('De orderbon kan niet gedownload worden omdat deze vervoerder/bezorgwijze daar niet voor is geconfigureerd. Neem contact op met OmniStock.', 'danger', true)
                            break
                        case '101':
                            this.$alert('De orderbon kan niet gedownload worden omdat deze vervoersmethode niet is gekoppeld aan een management-service. Neem contact op met OmniStock.', 'danger', true)
                            break
                        case 'ValidationError':
                            this.$alert(e.response.data?.errors?.[0]?.message, 'warning', false)
                            break
                        default:
                            this.$alert('Onbekende fout bij het ophalen van de orderbon.', 'danger', true)
                            break
                    }
                })
                .then(() => {
                    this.isPackingSlipRequested = false
                })
        },
    },
}
</script>
